import { useEffect } from 'react'
import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { PasswordInput } from '@/shared/ui/password-input'
import { Label } from '@/shared/ui/label'
import { t } from '@/shared/lib/i18n'
import { clearAllNewDepartmentSites } from '@/shared/lib/newDepartmentSiteSession'
import { newPasswordResetPath, sessionPath, ssoEntryPath } from '@/shared/lib/routes'
import { AuthShell } from './AuthShell'
import { CustomerSelector } from './CustomerSelector'

interface LoginPageProps {
  customerList?: { customerId: number; customerName: string }[]
  loginEmail?: string
  ssoEntryCode?: string | null
  errors?: Record<string, string[]>
  flash?: { alert?: string; notice?: string }
  [key: string]: unknown
}

const Login = () => {
  const {
    customerList,
    loginEmail,
    ssoEntryCode,
    errors: serverErrors,
    flash,
  } = usePage<LoginPageProps>().props

  // Sign-out, idle timeout and expired-JWT logouts land here (an SSO admin-kick does not).
  useEffect(() => {
    clearAllNewDepartmentSites()
  }, [])

  const form = useForm({ email: '', password: '', customer_id: '' })
  const formFieldErrors = form.errors as typeof form.errors & { base?: string | string[] }
  const baseFormError = formFieldErrors.base
  const errorMessage = (
    (Array.isArray(baseFormError) ? baseFormError[0] : baseFormError) ??
    serverErrors?.base?.[0] ??
    flash?.alert ??
    ''
  ).trim()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(sessionPath())
  }

  const hasCustomerList = Array.isArray(customerList) && customerList.length > 0

  if (hasCustomerList) {
    return (
      <AuthShell pageTitle={t('auth.customerSelector.title')} wide alignTop stretch>
        {loginEmail && (
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.customerSelector.signedInAs', { email: loginEmail })}
          </p>
        )}
        <CustomerSelector customerList={customerList} />
      </AuthShell>
    )
  }

  return (
    <AuthShell pageTitle={t('auth.login.title')}>
      <h1 className="text-2xl font-bold">{t('auth.login.title')}</h1>

      {flash?.notice?.trim() && (
        <div className="rounded-md border border-emerald-500/50 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash.notice}
        </div>
      )}

      {errorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      {ssoEntryCode && (
        <div className="space-y-4">
          <Button asChild variant="outline" className="w-full">
            <Link href={ssoEntryPath(ssoEntryCode)}>{t('auth.login.ssoButton')}</Link>
          </Button>
          <div className="flex items-center gap-3 text-xs uppercase text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            {t('auth.login.ssoDivider')}
            <span className="h-px flex-1 bg-border" />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('auth.login.emailLabel')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={form.data.email}
            onChange={(e) => form.setData('email', e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t('auth.login.passwordLabel')}</Label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={form.data.password}
            onChange={(e) => form.setData('password', e.target.value)}
            required
          />
          <div className="text-right">
            <Link
              href={newPasswordResetPath()}
              className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={form.processing}>
          {form.processing ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>
    </AuthShell>
  )
}

export default Login
